import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useServices, OWNER_DISPLAY_NAME } from '../app/services';
import { getMeta } from '../data/appMeta';
import { eraseLocalData } from '../data/db/erase';
import type { BudgetMember } from '../domain/model';
import { linkAccount } from './account';
import type { Account } from './cloud';
import { ACCESS_KEY, CURSOR_KEY, LAST_SYNC_KEY, countHeld, syncOnce } from './engine';
import { usePremium } from '../premium/PremiumProvider';
import { ACCESS_PREMIUM_KEY, readOnlySpaces } from './readOnly';
import { createInvite, joinBudget, leaveBudget, removeMember, type JoinResult } from './sharing';
import type { Invite } from './types';

/**
 * Keeps the device and the cloud in step while someone is signed in:
 * at start, shortly after every local write, when the app comes back to
 * the foreground or the network returns, and once a minute. The app never
 * waits for it; a failed round just leaves the outbox for the next one.
 */
export type SyncStatus = 'off' | 'idle' | 'syncing' | 'offline' | 'error';

interface SyncValue {
  /** This build has a cloud to sync with. */
  available: boolean;
  account: Account | null;
  status: SyncStatus;
  lastSyncedAt: number | null;
  /** Changes made on this device that the cloud does not have yet. */
  pending: number;
  /** Of those, the ones waiting for Premium (changes to your own budgets while cloud sync is off). */
  held: number;
  /** Shared budgets that are read-only now: their owner's Premium ended (see sync/readOnly.ts). */
  readOnly: ReadonlySet<string>;
  sendCode(email: string): Promise<void>;
  /** Signs in, links this install to the account, syncs, restarts the app on the result. */
  signIn(email: string, code: string): Promise<void>;
  signOut(): Promise<void>;
  /** Deletes the account on the server (see sync_account_delete), then this device's data; the app starts over. */
  deleteAccount(): Promise<void>;
  syncNow(): Promise<void>;
  /** Household sharing; every call needs an account (the server decides who may do what). */
  sharing: {
    invite(spaceId: string): Promise<Invite>;
    invites(spaceId: string): Promise<Invite[]>;
    revoke(code: string): Promise<void>;
    /** Join with a code; the budget comes down and opens. */
    join(code: string, displayName: string): Promise<JoinResult>;
    remove(member: BudgetMember): Promise<void>;
    leave(spaceId: string): Promise<void>;
  };
}

const Ctx = createContext<SyncValue | null>(null);

const WRITE_DELAY_MS = 1500;
const PERIOD_MS = 60_000;

export function SyncProvider({ children }: { children: ReactNode }) {
  const services = useServices();
  const { cloud, db, store, identity, dataVersion, notifyDataChanged, restart, activeSpace, setActiveSpace } = services;
  const [account, setAccount] = useState<Account | null>(null);
  const [status, setStatus] = useState<SyncStatus>('off');
  const [lastSyncedAt, setLastSyncedAt] = useState<number | null>(null);
  const [pending, setPending] = useState(0);
  const [held, setHeld] = useState(0);
  // Without Premium (or until the store has answered) your own budgets stay on this device; shared budgets still sync.
  const premium = usePremium();
  const holdOwnedBy = premium.ready && premium.has('sync') ? null : identity.session.userId;
  const [readOnly, setReadOnly] = useState<ReadonlySet<string>>(new Set());
  // Signed in, with the store's answer in: which shared budgets are read-only; the store refuses writes to them.
  const checkReadOnly = useCallback(async () => {
    const next = account && premium.ready ? await readOnlySpaces(db, identity.session.userId, premium.has('household')) : new Set<string>();
    store.setReadOnly(next.size ? (id) => next.has(id) : null);
    setReadOnly((prev) => (prev.size === next.size && [...next].every((id) => prev.has(id)) ? prev : next));
  }, [account, premium, db, identity, store]);
  useEffect(() => {
    void checkReadOnly();
  }, [checkReadOnly, dataVersion]);
  const running = useRef<Promise<void> | null>(null);
  const again = useRef(false);

  useEffect(() => {
    if (!cloud) return;
    let live = true;
    void Promise.all([cloud.account(), getMeta(db, LAST_SYNC_KEY)]).then(([a, last]) => {
      if (!live) return;
      setAccount(a);
      setStatus(a ? 'idle' : 'off');
      if (last) setLastSyncedAt(Number(last));
    });
    return () => {
      live = false;
    };
  }, [cloud, db]);

  const countPending = useCallback(async () => {
    const [row] = await db.all<{ n: number }>("SELECT COUNT(*) AS n FROM sync_outbox WHERE table_name <> 'users'");
    setPending(Number(row?.n ?? 0));
    setHeld(holdOwnedBy ? await countHeld(db, holdOwnedBy) : 0);
  }, [db, holdOwnedBy]);

  const syncNow = useCallback(async () => {
    if (!cloud || !account) return;
    // No connection: nothing to try. Changes wait in the outbox; the 'online' event brings us back.
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      setStatus('offline');
      void countPending();
      return;
    }
    if (running.current) {
      again.current = true;
      return running.current;
    }
    const round = (async () => {
      setStatus('syncing');
      try {
        do {
          again.current = false;
          const report = await syncOnce(db, cloud.transport, { holdOwnedBy });
          // Removed from the budget on screen (or left it on another device): start again elsewhere.
          if (activeSpace && report.lost.includes(activeSpace.id)) return restart();
          if (report.pulled > 0 || report.lost.length > 0) {
            // Still at onboarding and budgets just arrived from another device: open them.
            if (!activeSpace && (await store.spaces.listForUser(identity.session.userId)).length > 0) return restart();
            notifyDataChanged();
          }
        } while (again.current);
        setLastSyncedAt(Date.now());
        setStatus('idle');
      } catch (e) {
        console.warn('[sync]', e);
        setStatus(typeof navigator !== 'undefined' && navigator.onLine === false ? 'offline' : 'error');
      } finally {
        running.current = null;
        void countPending();
        // The server may have said an owner's Premium ended (or came back).
        void checkReadOnly();
      }
    })();
    running.current = round;
    return round;
  }, [cloud, account, db, notifyDataChanged, activeSpace, store, identity, restart, countPending, holdOwnedBy, checkReadOnly]);

  // Start, foreground, network back, and a slow heartbeat for changes made on other devices.
  useEffect(() => {
    if (!account) return;
    void syncNow();
    const onVisible = () => document.visibilityState === 'visible' && void syncNow();
    const onOnline = () => void syncNow();
    const onOffline = () => {
      setStatus('offline');
      void countPending();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    const timer = window.setInterval(() => document.visibilityState === 'visible' && void syncNow(), PERIOD_MS);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      window.clearInterval(timer);
    };
  }, [account, syncNow, countPending]);

  // Shortly after local writes (only when something is waiting: pulled data also bumps dataVersion).
  useEffect(() => {
    if (account) void countPending();
  }, [account, dataVersion, countPending]);
  useEffect(() => {
    if (!account) return;
    const timer = window.setTimeout(() => {
      void db.all<{ n: number }>('SELECT COUNT(*) AS n FROM sync_outbox').then((rows) => {
        if (Number(rows[0]?.n ?? 0) > 0) void syncNow();
      });
    }, WRITE_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [account, dataVersion, db, syncNow]);

  const value = useMemo<SyncValue>(
    () => ({
      available: Boolean(cloud),
      account,
      status,
      lastSyncedAt,
      pending,
      held,
      readOnly,
      syncNow,
      async sendCode(email) {
        await cloud!.sendCode(email.trim());
      },
      async signIn(email, code) {
        const a = await cloud!.verifyCode(email.trim(), code.trim());
        setAccount(a);
        setStatus('syncing');
        const link = await linkAccount(db, store, cloud!.transport, identity.session.userId, OWNER_DISPLAY_NAME);
        // Premium may have been bought under this account on another device.
        const e = await premium.refresh();
        await syncOnce(db, cloud!.transport, { holdOwnedBy: e.active ? null : link.userId });
        // Another device's identity (and possibly its budgets): start again from the database.
        if (link.joined) restart();
        else {
          setLastSyncedAt(Date.now());
          setStatus('idle');
          notifyDataChanged();
        }
      },
      async deleteAccount() {
        await cloud!.transport.deleteAccount();
        // The server has already forgotten this sign-in; clearing the session here cannot fail the deletion.
        await cloud!.signOut().catch((e: unknown) => console.warn('[account] sign-out after delete', e));
        await eraseLocalData(db);
        restart();
      },
      async signOut() {
        await cloud!.signOut();
        // A later sign-in (maybe another account) starts its pulls from scratch; the data stays here.
        await db.run('DELETE FROM app_meta WHERE key IN (?, ?, ?)', [CURSOR_KEY, ACCESS_KEY, ACCESS_PREMIUM_KEY]);
        setAccount(null);
        setStatus('off');
        // Premium that came with the account goes with it.
        void premium.refresh();
      },
      sharing: {
        invite: (spaceId) => createInvite(db, cloud!.transport, spaceId, { holdOwnedBy }),
        invites: (spaceId) => cloud!.transport.listInvites(spaceId),
        revoke: (code) => cloud!.transport.revokeInvite(code),
        async join(code, displayName) {
          const res = await joinBudget(db, store, cloud!.transport, { code, userId: identity.session.userId, displayName }, { holdOwnedBy });
          if (res.ok) {
            const space = await store.spaces.get(res.spaceId);
            if (space) await setActiveSpace(space);
            setLastSyncedAt(Date.now());
            notifyDataChanged();
          }
          return res;
        },
        async remove(member) {
          await removeMember(store, cloud!.transport, member);
          notifyDataChanged();
          void syncNow();
        },
        async leave(spaceId) {
          await leaveBudget(db, store, cloud!.transport, spaceId, identity.session.userId, { holdOwnedBy });
          if (activeSpace?.id !== spaceId) return notifyDataChanged();
          const next = (await store.spaces.listForUser(identity.session.userId))[0];
          if (next) {
            await setActiveSpace(next);
            notifyDataChanged();
          } else restart();
        },
      },
    }),
    [
      cloud,
      account,
      status,
      lastSyncedAt,
      pending,
      held,
      readOnly,
      syncNow,
      db,
      store,
      identity,
      restart,
      notifyDataChanged,
      activeSpace,
      setActiveSpace,
      premium,
      holdOwnedBy,
    ],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSync(): SyncValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useSync outside SyncProvider');
  return v;
}
